// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SparklineChart.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import { observer } from 'mobx-react';
import React, { Component } from 'react';
import { getOr, nth, get, sumBy, toNumber, size, map, slice, concat, head, omit, mapValues } from 'lodash/fp';
import { parser as chartDataParser } from 'share/utils/Izenda.ChartDataParser';
import SimpleChart from './Izenda.SimpleChart';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import ChartPointAction from './Izenda.ChartPointAction';
import { ContainerName, PAGE_SIZE_BACK_SIDE } from 'logic/data/Izenda.ReportPartChartConstants';
import Highcharts from 'highcharts';
import jsFormatService from 'share/Izenda.JsFormatService';

const X_AXIS_CONTAINER_NAME = 'labels';
const Y_AXIS_CONTAINER_NAME = 'values';
const SEPARATOR_CONTAINER_NAME = 'separators';
const DEFAULT_HEIGHT_SIZE = 40;
const DEFAULT_FONT_SIZE = 14;
const DEFAULT_PADDING = 10;

const SPARKLINE_CHART_OPTS = {
  commonOptions: {
    izendaHiddenAllAxis: true,
    izMargin: 0,
    'izLegend.visibility': false,
    'izMarker.radius': 1,
    izChartStyle: { overflow: 'visible' },

    /* tooltip */
    'izTooltip.enabled': true,
    'izTooltip.borderWidth': 0,
    'izTooltip.hideDelay': 0,
    'izTooltip.shared': true,
    'izTooltip.positioner': (w, h, point) => ({ x: 0, y: -h / 2 }),
    izFillContainer: true
  },
  optionByType: {
    izValueLabel: false,
    izInverted: false,
    izStacking: null,
    izMultiColor: false
  },
  commonXYAxis: {
    /* Sparkline does not support Threshold line. So we need to reset */
    allDynamicThresholdLine: [],
    metricAllPlotLinesOption: [],
    /* End */
    xAxis: {
      izTickPositions: [],
      izStartOnTick: false,
      izEndOnTick: false,
      'izLabel.enabled': false,
      'izTitle.enabled': false,
      'izTitle.text': ''
    },
    yAxis: {
      izTickPositions: [0],
      izStartOnTick: false,
      izEndOnTick: false,
      'izLabel.enabled': false,
      'izTitle.enabled': false,
      'izTitle.text': ''
    },
    commonXYAxis: {
      izGridLine: null
    }
  }
};

const COLUMN_TYPE = {
  Total: 'Total',
  Chart: 'Chart',
  Separator: 'Separator'
};

class SparklineChart extends Component {
  /**
   * Render by field
   * @param {any} fieldInfo
   * @param {any} cellSize
   * @param {Function} getContentStyle
   */
  renderByField(fieldInfo: any, cellSize: any, getContentStyle: () => mixed) {
    const props = this.props;
    const { chartData: dataParser, seriesOptions, chartOptions } = props;
    const dataStructure = dataParser.dataStructure;
    const cols = [];

    const fieldName = fieldInfo.fieldNameAlias;
    const fieldConfig = getOr({}, fieldName, seriesOptions);
    const columnProps = {
      fnGetCellContentStyle: () => getContentStyle(false),
      resizable: false
    };

    // add [sum] column (if any)
    if (fieldConfig.showTotal) {
      cols.push(
        <Column
          {...columnProps}
          columnType={COLUMN_TYPE.Total}
          title={'Total of ' + fieldName}
          className="align-right"
          align="center"
          cell={renderSumValue}
        />
      );
    }

    // render chart
    columnProps.fnGetCellContentStyle = () => getContentStyle(true);

    cols.push(<Column {...columnProps} columnType={COLUMN_TYPE.Chart} title={fieldName} cell={renderChart} />);

    return cols;

    function renderChart(rowData) {
      const dtStructure = $izenda.extend(true, {}, dataStructure);
      dtStructure.values = [fieldInfo];
      const xAxisField = nth(dataParser.expandedLevel, dtStructure[X_AXIS_CONTAINER_NAME]);

      // Clone and remove dynamic threshold because Sparkine No need Threshold
      const cloneSeriesOptions = mapValues( (serie) => {
        return omit([ContainerName.DYNAMIC_THRESHOLD,'thresholds'], serie);
      }, { ...seriesOptions });

      const field = xAxisField && cloneSeriesOptions[xAxisField.fieldNameAlias];
      field && (field.axisOption = { ...field.axisOption, izCategory: true });

      return (
        <SimpleChart
          {...props}
          seriesOptions={cloneSeriesOptions}
          chartOptions={$izenda.extend(true, {}, chartOptions, SPARKLINE_CHART_OPTS)}
          chartType={get('chartOptions.chartType', fieldConfig)}
          chartData={chartDataParser(null, null, { fieldsMapping: null, records: rowData.data }, dtStructure)}
        />
      );
    }

    function renderSumValue(rowData) {
      const source = dataParser.getDataByField(fieldInfo, rowData.data);
      const jsFormatString = get('reportPartElm.properties.dataFormattings.format.jsFormatString', fieldInfo);
      const totalValue = sumBy(toNumber, source.data);
      const jsFormatId = jsFormatString
        ? get('reportPartElm.properties.dataFormattings.format.formatId', fieldInfo)
        : null;
      let sum = jsFormatString ? jsFormatService.format(jsFormatId, totalValue) : totalValue;

      return <span>{dataParser.getFormatData(sum, fieldConfig)}</span>;
    }
  }

  /**
   * Get cell size
   * @param {any} containerSize
   * @param {number} numRows
   * @param {number} numCols
   */
  getCellSize(containerSize: any, numRows: number, numCols: number) {
    const containerHeight = containerSize.height;
    const containerWidth = containerSize.width;

    return {
      width: containerWidth / numCols,
      height: Math.min(DEFAULT_HEIGHT_SIZE, containerHeight / (numRows + 2))
    };
  }

  render() {
    const {
      chartOptions,
      seriesOptions,
      chartData: dataParser,
      printingMode,
      isPreview,
      containerSize: { height, width }
    } = this.props;
    const dataStructure = dataParser.dataStructure;
    const hasSeparator = dataParser.hasSeparator(SEPARATOR_CONTAINER_NAME);
    const rowData = parseData() || [];
    const containerSize = {
      height: height - (printingMode ? 0 : DEFAULT_HEIGHT_SIZE * 2),
      width: width
    };
    const columns = getColumns.call(this);
    let cellSize;

    addSeparatorColumn(columns);
    cellSize = this.getCellSize(containerSize, size(rowData), size(columns));

    return renderUseTable(rowData, columns);

    function renderUseTable(rowData, columns) {
      const tableStyle = {
        fontSize: DEFAULT_FONT_SIZE * (cellSize.height / DEFAULT_HEIGHT_SIZE)
      };
      const colWidth = Math.ceil(containerSize.width / size(columns));
      const cols = map(col => React.cloneElement(col, { width: colWidth }), columns);

      return (
        <Table
          dataSource={rowData}
          staticRender={true}
          className="simple-table"
          tableStyle={tableStyle}
          showScroll={false}
        >
          {cols}
        </Table>
      );
    }

    function getContentStyle(isChartColumn) {
      const padding = Math.ceil(DEFAULT_PADDING * (cellSize.height / DEFAULT_HEIGHT_SIZE) * (isChartColumn ? 0.5 : 1));
      return {
        paddingTop: padding,
        paddingBottom: padding,
        height: Math.ceil(cellSize.height) + 'px'
      };
    }

    function parseData() {
      let groupNames = [];
      let groupedData = dataParser.groupData(SEPARATOR_CONTAINER_NAME, groupNames);

      if (!hasSeparator) {
        groupNames = ['ALL'];
        groupedData = {
          ALL: groupedData
        };
      }

      const groupArr = groupNames.map(groupName => {
        return {
          name: groupName,
          data: groupedData[groupName]
        };
      });

      if (isPreview && !printingMode) {
        return slice(0, PAGE_SIZE_BACK_SIDE, groupArr);
      }

      return groupArr;
    }

    function getColumns() {
      let columns = [];
      dataStructure[Y_AXIS_CONTAINER_NAME].forEach(field => {
        columns = concat(columns, this.renderByField(field, cellSize, getContentStyle) || []);
      });

      return columns;
    }

    function addSeparatorColumn(columns) {
      if (!hasSeparator) {
        return columns;
      }

      columns.unshift(getSeparatorColumn());

      return columns;

      function getSeparatorColumn() {
        const separatorInfo = dataParser.getSeparatorInfo(SEPARATOR_CONTAINER_NAME);
        const separatorOptions = getOr({}, separatorInfo.fieldNameAlias, seriesOptions);
        const pointAction = get('commonActions.pointAction', chartOptions);

        return (
          <Column
            columnType={COLUMN_TYPE.Separator}
            title={separatorInfo.fieldNameAlias}
            cell={renderSeparator}
            resizable={false}
            fnGetCellContentStyle={getSeparatorContentStyle}
          />
        );

        function getSeparatorContentStyle() {
          return getContentStyle(false);
        }

        function renderSeparator(rowData) {
          const fieldSource = {
            element: separatorInfo,
            record: head(rowData.data)
          };

          return (
            <ChartPointAction
              value={rowData && rowData.name}
              fieldName={separatorInfo.fieldNameAlias}
              configField={separatorOptions}
              fieldSource={fieldSource}
              pointAction={pointAction}
            />
          );
        }
      }
    }
  }
}

export default observer(SparklineChart);



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.SparklineChart.js